import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Box, Flex, Text, Button, IconButton, Input, HStack, VStack, SimpleGrid,
    Badge, Tooltip, useToast, Spinner, Avatar, Divider, Skeleton, Select,
    Switch, FormControl, FormLabel, Slider, SliderTrack, SliderFilledTrack, SliderThumb,
    Tabs, TabList, Tab, TabPanels, TabPanel, Checkbox, Textarea, AlertDialog,
    AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
    useDisclosure, Progress,
} from "@chakra-ui/react";
import {
    Phone, PhoneOff, PhoneIncoming, PhoneOutgoing, PhoneCall, PhoneMissed,
    Mic, MicOff, Pause, Play, Delete, User, Clock, Link as LinkIcon,
    Power, RefreshCw, History, AlertCircle, Settings, Bell, Volume2, Calendar,
    Sparkles, Radio, Activity, Headphones, Save, FileText, CheckCircle2,
} from "lucide-react";
import { useSoftphone, STATUS_FINAIS } from "../contexts/softphone";
import {
    listarSoftphoneChamadas, getSoftphoneChamada, transcreverSoftphoneChamada,
    listarSoftphoneUsuariosComChamadas, getSoftphoneAudioBlob,
} from "../services/api";

const STATUS_COLOR = {
    desconectado: "gray", conectando: "yellow", conectado: "yellow", registrado: "green",
};
const STATUS_LABEL = {
    desconectado: "Desconectado", conectando: "Conectando", conectado: "Conectado", registrado: "Online",
};

const CHAMADA_STATUS_BADGE = {
    atendida: { label: "Atendida", color: "green" },
    em_andamento: { label: "Em andamento", color: "blue" },
    perdida: { label: "Perdida", color: "red" },
    recusada: { label: "Recusada", color: "orange" },
    encerrada: { label: "Encerrada", color: "gray" },
    cancelada: { label: "Cancelada", color: "gray" },
    falhou: { label: "Falhou", color: "red" },
    iniciando: { label: "Iniciando", color: "yellow" },
    tocando: { label: "Tocando", color: "blue" },
    chamando: { label: "Chamando", color: "blue" },
    discando: { label: "Discando", color: "blue" },
};

const ContentShell = ({ children }) => (
    // pt separado do px/pb: com `p` antes de `pt` funcionava por sorte — trocar
    // a ordem das props colava a tela no menu, em silêncio.
    <Box ml={{ base: "0", md: "64px" }} pt="70px" px={{ base: 3, md: 6 }} pb={{ base: 3, md: 6 }} minH="100vh" bg="gray.50">
        <Box maxW="1400px" mx="auto">{children}</Box>
    </Box>
);

const fmtDuration = (sec) => {
    if (sec == null) return "—";
    const m = Math.floor(sec / 60).toString().padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
};
const fmtTime = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); } catch (_) { return ""; } };
const fmtNumero = (raw) => {
    const n = String(raw || "").replace(/\D/g, "");
    if (n.length === 11) return `(${n.slice(0,2)}) ${n.slice(2,7)}-${n.slice(7)}`;
    if (n.length === 10) return `(${n.slice(0,2)}) ${n.slice(2,6)}-${n.slice(6)}`;
    if (n.length === 8 || n.length === 9) return n.replace(/(\d{4,5})(\d{4})/, "$1-$2");
    return raw || "—";
};

const TECLADO = [["1","2","3"],["4","5","6"],["7","8","9"],["*","0","#"]];
const DIAS = [
    { id: 1, label: "Seg" }, { id: 2, label: "Ter" }, { id: 3, label: "Qua" },
    { id: 4, label: "Qui" }, { id: 5, label: "Sex" }, { id: 6, label: "Sáb" }, { id: 7, label: "Dom" },
];

const Discador = () => {
    const toast = useToast();
    const sp = useSoftphone();

    const [historico, setHistorico] = useState([]);
    const [loadingHist, setLoadingHist] = useState(false);
    const [numeroDigitado, setNumeroDigitado] = useState("");

    const carregarHistorico = useCallback(async () => {
        setLoadingHist(true);
        const res = await listarSoftphoneChamadas({ minhas: 1, per_page: 30 });
        if (res?.data) setHistorico(res.data);
        setLoadingHist(false);
    }, []);

    useEffect(() => { carregarHistorico(); }, [carregarHistorico]);

    // Recarregar histórico quando chamada termina
    useEffect(() => {
        if (STATUS_FINAIS.includes(sp.call?.status)) {
            const t = setTimeout(carregarHistorico, 1500);
            return () => clearTimeout(t);
        }
    }, [sp.call?.status, carregarHistorico]);

    const handleDigitar = (c) => setNumeroDigitado((n) => n + c);
    const handleBackspace = () => setNumeroDigitado((n) => n.slice(0, -1));
    const handleLimpar = () => setNumeroDigitado("");

    const handleDiscar = () => {
        const num = numeroDigitado.replace(/\D/g, "");
        if (!num) { toast({ status: "warning", title: "Digite um número" }); return; }
        if (!sp.registered) { toast({ status: "warning", title: "Conecte o softphone antes de discar" }); return; }
        sp.dial(num);
    };

    const inCall = !!sp.call && !STATUS_FINAIS.includes(sp.call.status);
    // Quem atende a ligação hoje é o PABX próprio; "NVOIP" só quando o ramal
    // ainda é o da operadora.
    const modoPbx = sp.modo === "pbx";
    const statusColor = STATUS_COLOR[sp.status] || "gray";
    const statusLabel = STATUS_LABEL[sp.status] || sp.status;
    const perdidasHoje = useMemo(() => {
        const hoje = new Date().toDateString();
        return historico.filter(c => {
            if (c.direcao !== "entrada") return false;
            if (!["perdida", "recusada"].includes(c.status)) return false;
            const d = new Date(c.iniciada_em || c.created_at);
            return d.toDateString() === hoje;
        }).length;
    }, [historico]);

    return (
        <ContentShell>
            <Flex justify="space-between" align="center" mb={5} flexWrap="wrap" gap={3}>
                <Box>
                    <HStack>
                        <Phone size={24} color="#3b82f6" />
                        <Text fontSize="xl" fontWeight="bold" color="gray.800">
                            {modoPbx ? "Discador" : "Discador NVOIP"}
                        </Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                        {modoPbx
                            ? "Softphone web — faz e recebe ligações pelo PABX da TAGA."
                            : "Softphone web — faz e recebe ligações pela NVOIP."}
                    </Text>
                </Box>
                <HStack>
                    {perdidasHoje > 0 && (
                        <Tooltip label="Chamadas que tocaram e não foram atendidas hoje">
                            <Badge colorScheme="red" fontSize="11px" borderRadius="md" px={2} py={1}>
                                <HStack spacing={1}><PhoneMissed size={10} /><Text>{perdidasHoje} perdida{perdidasHoje > 1 ? "s" : ""} hoje</Text></HStack>
                            </Badge>
                        </Tooltip>
                    )}
                    <Badge colorScheme={statusColor} fontSize="11px" borderRadius="md" px={2} py={1}>{statusLabel}</Badge>
                    {sp.registered && (
                        <Badge colorScheme={sp.heartbeatOk ? "green" : "yellow"} fontSize="11px" borderRadius="md" px={2} py={1}>
                            <HStack spacing={1}><Activity size={10} /><Text>{sp.heartbeatOk ? "Saudável" : "Sem batimento"}</Text></HStack>
                        </Badge>
                    )}
                    {sp.credentials?.numbersip && (
                        <Badge colorScheme="blue" variant="outline" fontSize="11px" borderRadius="md" px={2} py={1}>
                            Ramal {sp.credentials.numbersip}
                        </Badge>
                    )}
                    {!sp.registered ? (
                        <Button size="sm" colorScheme="green" leftIcon={<Power size={14} />} onClick={sp.connect} isLoading={sp.status === "conectando" || sp.status === "conectado"} isDisabled={!sp.credentials}>Conectar</Button>
                    ) : (
                        <Button size="sm" colorScheme="red" variant="outline" leftIcon={<Power size={14} />} onClick={sp.disconnect}>Desconectar</Button>
                    )}
                </HStack>
            </Flex>

            {sp.credError && (
                <Box bg="red.50" border="1px solid" borderColor="red.200" p={4} borderRadius="md" mb={4}>
                    <HStack><AlertCircle size={16} color="#b91c1c" /><Text fontSize="sm" color="red.700" fontWeight="semibold">{sp.credError}</Text></HStack>
                    {/* No modo PABX o ramal é provisionado pelo administrador
                        (php artisan pbx:ramais): a tela de Usuários não tem esse
                        campo, e mandar a pessoa para lá não resolvia nada. */}
                    <Text fontSize="xs" color="red.600" mt={2}>
                        {/pabx/i.test(String(sp.credError))
                            ? "O ramal do PABX é liberado pelo administrador do sistema — não é campo da tela de Usuários. Peça para provisionarem o seu ramal."
                            : <>Cadastre seu NumberSIP e Senha SIP em <RouterLink to="/usuarios" style={{ textDecoration: "underline" }}>Sistema › Usuários</RouterLink>.</>}
                    </Text>
                </Box>
            )}
            {sp.error && (
                <Box bg="red.50" border="1px solid" borderColor="red.200" p={3} borderRadius="md" mb={4}>
                    <HStack><AlertCircle size={14} color="#b91c1c" /><Text fontSize="xs" color="red.700">{sp.error}</Text></HStack>
                </Box>
            )}

            <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden">
                <Tabs variant="line" colorScheme="blue" size="md" isLazy>
                    <TabList borderBottomWidth="1px" borderColor="gray.200" px={{ base: 2, md: 4 }} overflowX="auto">
                        <Tab fontSize="sm"><Phone size={14} style={{ marginRight: 6 }} />Discador</Tab>
                        <Tab fontSize="sm"><History size={14} style={{ marginRight: 6 }} />Histórico</Tab>
                        <Tab fontSize="sm"><Settings size={14} style={{ marginRight: 6 }} />Configurações</Tab>
                    </TabList>
                    <TabPanels>
                        <TabPanel p={{ base: 3, md: 5 }}>
                            <DiscadorPanel
                                numeroDigitado={numeroDigitado}
                                setNumeroDigitado={setNumeroDigitado}
                                handleDigitar={handleDigitar}
                                handleBackspace={handleBackspace}
                                handleLimpar={handleLimpar}
                                handleDiscar={handleDiscar}
                                historico={historico}
                                loadingHist={loadingHist}
                                carregarHistorico={carregarHistorico}
                                sp={sp}
                                inCall={inCall}
                            />
                        </TabPanel>
                        <TabPanel p={{ base: 3, md: 5 }}>
                            <HistoricoPanel sp={sp} />
                        </TabPanel>
                        <TabPanel p={{ base: 3, md: 5 }}>
                            <ConfiguracoesPanel sp={sp} />
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Box>
        </ContentShell>
    );
};

// ===================== Aba Discador =====================
const DiscadorPanel = ({ numeroDigitado, setNumeroDigitado, handleDigitar, handleBackspace, handleLimpar, handleDiscar, historico, loadingHist, carregarHistorico, sp, inCall }) => (
    <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
        {/* Histórico curto */}
        <Box bg="gray.50" borderRadius="lg" overflow="hidden" gridColumn={{ base: "auto", lg: "span 1" }}>
            <Flex px={3} py={2} borderBottom="1px solid" borderColor="gray.200" align="center" justify="space-between" bg="white">
                <HStack><History size={14} color="#475569" /><Text fontSize="sm" fontWeight="bold" color="gray.700">Recentes</Text></HStack>
                <Tooltip label="Atualizar"><IconButton size="xs" variant="ghost" icon={<RefreshCw size={12} />} onClick={carregarHistorico} aria-label="atualizar" /></Tooltip>
            </Flex>
            <Box maxH="500px" overflowY="auto" bg="white">
                {loadingHist ? (
                    <Box p={3}><Skeleton height="30px" mb={2} /><Skeleton height="30px" mb={2} /><Skeleton height="30px" /></Box>
                ) : historico.length === 0 ? (
                    <Text textAlign="center" fontSize="xs" color="gray.400" py={6}>Sem chamadas</Text>
                ) : (
                    <VStack align="stretch" spacing={0}>
                        {historico.slice(0, 15).map((c) => {
                            const ent = c.direcao === "entrada";
                            const badge = CHAMADA_STATUS_BADGE[c.status] || { label: c.status, color: "gray" };
                            const Icon = ent ? (c.status === "perdida" ? PhoneMissed : PhoneIncoming) : PhoneOutgoing;
                            const iconColor = c.status === "perdida" ? "#ef4444" : ent ? "#10b981" : "#3b82f6";
                            return (
                                <Box key={c.id} px={3} py={2} borderBottom="1px solid" borderColor="gray.50" _hover={{ bg: "gray.50" }} cursor="pointer" onClick={() => setNumeroDigitado(c.numero)}>
                                    <HStack align="start" spacing={2}>
                                        <Box mt={1}><Icon size={13} color={iconColor} /></Box>
                                        <Box flex={1} minW={0}>
                                            <Text fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={1}>{c.cliente?.nome || fmtNumero(c.numero)}</Text>
                                            <HStack spacing={2}>
                                                <Text fontSize="10px" color="gray.500">{fmtTime(c.iniciada_em || c.created_at)}</Text>
                                                {c.duracao_seg > 0 && <Text fontSize="10px" color="gray.500">• {fmtDuration(c.duracao_seg)}</Text>}
                                                <Badge colorScheme={badge.color} fontSize="9px" borderRadius="sm">{badge.label}</Badge>
                                            </HStack>
                                        </Box>
                                    </HStack>
                                </Box>
                            );
                        })}
                    </VStack>
                )}
            </Box>
        </Box>

        {/* Teclado / chamada ativa */}
        <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" p={4}>
            {inCall ? (
                <CallActive sp={sp} />
            ) : (
                <TecladoView
                    numero={numeroDigitado}
                    onDigit={handleDigitar}
                    onBackspace={handleBackspace}
                    onLimpar={handleLimpar}
                    onDial={handleDiscar}
                    registered={sp.registered}
                    onSetNumero={setNumeroDigitado}
                />
            )}
        </Box>

        {/* Cliente */}
        <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
            <Flex px={3} py={2} borderBottom="1px solid" borderColor="gray.100" align="center">
                <HStack><User size={14} color="#475569" /><Text fontSize="sm" fontWeight="bold" color="gray.700">Cliente</Text></HStack>
            </Flex>
            <Box p={4}>
                {sp.activeCliente ? (
                    <VStack align="stretch" spacing={3}>
                        <HStack>
                            <Avatar size="md" name={sp.activeCliente.nome} />
                            <Box minW={0}>
                                <Text fontSize="md" fontWeight="bold" color="gray.800" noOfLines={2}>{sp.activeCliente.nome}</Text>
                                <Text fontSize="xs" color="gray.500">{sp.activeCliente.documento || "—"}</Text>
                            </Box>
                        </HStack>
                        <Divider />
                        <Box><Text fontSize="10px" color="gray.500" fontWeight="semibold" textTransform="uppercase">Telefone</Text><Text fontSize="sm" color="gray.700">{sp.activeCliente.telefone || "—"}</Text></Box>
                        <Box><Text fontSize="10px" color="gray.500" fontWeight="semibold" textTransform="uppercase">Email</Text><Text fontSize="sm" color="gray.700">{sp.activeCliente.email || "—"}</Text></Box>
                        <Button as={RouterLink} to={`/clientes?cliente_id=${sp.activeCliente.id}`} size="xs" leftIcon={<LinkIcon size={12} />} variant="outline" colorScheme="blue">Abrir perfil</Button>
                    </VStack>
                ) : sp.call ? (
                    <Box textAlign="center" py={6}>
                        <Text fontSize="sm" color="gray.500">Número não vinculado a cliente</Text>
                        <Text fontSize="xs" color="gray.400" mt={1}>{fmtNumero(sp.call?.remote || "")}</Text>
                    </Box>
                ) : (
                    <Box textAlign="center" py={6}><Text fontSize="sm" color="gray.400">Sem chamada ativa</Text></Box>
                )}
            </Box>
        </Box>
    </SimpleGrid>
);

const TecladoView = ({ numero, onDigit, onBackspace, onLimpar, onDial, registered, onSetNumero }) => (
    <VStack spacing={4} align="stretch">
        <Input value={numero} onChange={(e) => onSetNumero(e.target.value.replace(/[^\d*#+]/g, ""))} placeholder="Digite o número" size="lg" fontSize="2xl" textAlign="center" fontFamily="mono" fontWeight="semibold" borderRadius="md" bg="gray.50" />
        <SimpleGrid columns={3} spacing={2}>
            {TECLADO.flat().map((d) => (
                <Button key={d} h="55px" fontSize="xl" fontWeight="bold" variant="outline" onClick={() => onDigit(d)}>{d}</Button>
            ))}
        </SimpleGrid>
        <HStack>
            <Button flex={1} size="md" variant="ghost" onClick={onBackspace} leftIcon={<Delete size={14} />}>Apagar</Button>
            <Button flex={1} size="md" variant="ghost" onClick={onLimpar}>Limpar</Button>
        </HStack>
        <Button size="lg" colorScheme="green" leftIcon={<PhoneCall size={18} />} onClick={onDial} isDisabled={!registered || !numero} h="55px">Discar</Button>
        {!registered && <Text fontSize="xs" color="orange.500" textAlign="center">Clique em "Conectar" no topo</Text>}
    </VStack>
);

const CallActive = ({ sp }) => {
    const call = sp.call;
    const cliente = sp.activeCliente;
    const statusInfo = CHAMADA_STATUS_BADGE[call.status] || { label: call.status, color: "gray" };
    const isIncoming = call.direction === "incoming";
    const Icon = isIncoming ? PhoneIncoming : PhoneOutgoing;
    const naoAtendida = !call.answeredAt;
    const [showDtmf, setShowDtmf] = useState(false);

    return (
        <VStack spacing={4} align="stretch" py={2}>
            <Box textAlign="center">
                <HStack justify="center" mb={2}>
                    <Icon size={18} color={isIncoming ? "#10b981" : "#3b82f6"} />
                    <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase">{isIncoming ? "Chamada Recebida" : "Chamada de Saída"}</Text>
                </HStack>
                <Avatar size="xl" name={cliente?.nome || call.remote} mb={2} />
                <Text fontSize="lg" fontWeight="bold" color="gray.800" noOfLines={1}>{cliente?.nome || fmtNumero(call.remote)}</Text>
                {cliente && <Text fontSize="sm" color="gray.500">{fmtNumero(call.remote)}</Text>}
                <Badge colorScheme={statusInfo.color} fontSize="11px" borderRadius="md" px={2} py={1} mt={2}>{statusInfo.label}</Badge>
                {call.answeredAt && (
                    <HStack justify="center" mt={2}>
                        <Clock size={14} color="#64748b" />
                        <Text fontSize="md" fontWeight="semibold" color="gray.700" fontFamily="mono">{fmtDuration(call.durationSec)}</Text>
                        {sp.recording && <Badge colorScheme="red" fontSize="10px"><HStack spacing={1}><Radio size={10} /><Text>Gravando</Text></HStack></Badge>}
                    </HStack>
                )}
            </Box>
            <Divider />
            {isIncoming && naoAtendida ? (
                <HStack spacing={3}>
                    <Button flex={1} size="lg" colorScheme="green" leftIcon={<Phone size={18} />} onClick={sp.answer} h="55px">Atender</Button>
                    <Button flex={1} size="lg" colorScheme="red" leftIcon={<PhoneOff size={18} />} onClick={sp.hangup} h="55px">Recusar</Button>
                </HStack>
            ) : (
                <>
                    <HStack spacing={2}>
                        <Tooltip label={call.muted ? "Ativar mic" : "Silenciar"}>
                            <IconButton flex={1} size="lg" h="50px" colorScheme={call.muted ? "red" : "gray"} variant="outline" icon={call.muted ? <MicOff size={18} /> : <Mic size={18} />} onClick={sp.toggleMute} aria-label="mute" />
                        </Tooltip>
                        <Tooltip label={call.onHold ? "Retomar" : "Pausar"}>
                            <IconButton flex={1} size="lg" h="50px" colorScheme={call.onHold ? "yellow" : "gray"} variant="outline" icon={call.onHold ? <Play size={18} /> : <Pause size={18} />} onClick={sp.toggleHold} aria-label="hold" />
                        </Tooltip>
                        <Tooltip label="Teclado DTMF">
                            <IconButton flex={1} size="lg" h="50px" variant="outline" icon={<Phone size={18} />} onClick={() => setShowDtmf((v) => !v)} aria-label="dtmf" />
                        </Tooltip>
                        {call.answeredAt && (
                            sp.gravacaoNoServidor ? (
                                <Tooltip label="O PABX grava esta ligação do início ao fim — nada a fazer aqui.">
                                    <IconButton flex={1} size="lg" h="50px" colorScheme="red" variant="solid" isDisabled icon={<Radio size={18} />} aria-label="rec-servidor" />
                                </Tooltip>
                            ) : sp.recording ? (
                                <Tooltip label="Parar gravação"><IconButton flex={1} size="lg" h="50px" colorScheme="red" variant="solid" icon={<Radio size={18} />} onClick={sp.stopRecording} aria-label="rec-stop" /></Tooltip>
                            ) : (
                                <Tooltip label="Gravar (com Whisper)"><IconButton flex={1} size="lg" h="50px" colorScheme="red" variant="outline" icon={<Radio size={18} />} onClick={sp.startRecording} aria-label="rec" /></Tooltip>
                            )
                        )}
                    </HStack>
                    {showDtmf && (
                        <SimpleGrid columns={3} spacing={2}>
                            {TECLADO.flat().map((d) => <Button key={d} size="md" variant="ghost" onClick={() => sp.sendDtmf(d)}>{d}</Button>)}
                        </SimpleGrid>
                    )}
                    <Button size="lg" colorScheme="red" leftIcon={<PhoneOff size={18} />} onClick={sp.hangup} h="55px">Encerrar</Button>
                </>
            )}
        </VStack>
    );
};

// ===================== Aba Histórico =====================
const HistoricoPanel = ({ sp }) => {
    const toast = useToast();
    const [transcribingId, setTranscribingId] = useState(null);
    const [detalhe, setDetalhe] = useState({ open: false, chamada: null });
    const [filtro, setFiltro] = useState("todas");
    const [escopo, setEscopo] = useState("minhas"); // minhas | usuario:N | todos (admin only)
    const [usuarios, setUsuarios] = useState([]);
    const [historico, setHistorico] = useState([]);
    const [loadingHist, setLoadingHist] = useState(false);

    const carregar = useCallback(async () => {
        setLoadingHist(true);
        const params = { per_page: 100 };
        if (sp.isAdmin) {
            if (escopo === "minhas") params.minhas = 1;
            else if (escopo.startsWith("usuario:")) params.user_id = Number(escopo.split(":")[1]);
            // escopo "todos" → sem filtro
        } else {
            params.minhas = 1;
        }
        const res = await listarSoftphoneChamadas(params);
        if (res?.data) setHistorico(res.data);
        setLoadingHist(false);
    }, [sp.isAdmin, escopo]);

    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => {
        if (!sp.isAdmin) return;
        (async () => {
            const r = await listarSoftphoneUsuariosComChamadas();
            if (r?.data) setUsuarios(r.data);
        })();
    }, [sp.isAdmin]);

    const contadores = useMemo(() => {
        const c = { total: historico.length, perdidas: 0, atendidas: 0, saidas: 0 };
        historico.forEach((x) => {
            if (x.direcao === "entrada" && ["perdida", "recusada"].includes(x.status)) c.perdidas++;
            else if (["atendida", "encerrada", "em_andamento"].includes(x.status)) c.atendidas++;
            if (x.direcao === "saida") c.saidas++;
        });
        return c;
    }, [historico]);

    const lista = useMemo(() => {
        if (filtro === "perdidas") return historico.filter(x => x.direcao === "entrada" && ["perdida", "recusada"].includes(x.status));
        if (filtro === "atendidas") return historico.filter(x => ["atendida", "encerrada", "em_andamento"].includes(x.status));
        if (filtro === "saidas") return historico.filter(x => x.direcao === "saida");
        return historico;
    }, [historico, filtro]);

    const handleTranscrever = async (c) => {
        setTranscribingId(c.id);
        const res = await transcreverSoftphoneChamada(c.id, null);
        setTranscribingId(null);
        if (res?.data?.transcricao) {
            toast({ status: "success", title: "Transcrição concluída" });
            carregar();
            abrirDetalhe(c);
        } else {
            toast({ status: "error", title: res?.error || "Sem áudio gravado para transcrever" });
        }
    };

    const abrirDetalhe = async (c) => {
        const r = await getSoftphoneChamada(c.id);
        setDetalhe({ open: true, chamada: r?.data || c });
    };

    return (
        <Box>
            {sp.isAdmin && (
                <HStack mb={4} spacing={3} flexWrap="wrap">
                    <Box>
                        <Text fontSize="10px" color="gray.500" fontWeight="semibold" textTransform="uppercase" mb={1}>Visualizar chamadas de</Text>
                        <Select size="sm" value={escopo} onChange={(e) => setEscopo(e.target.value)} w="240px" bg="white">
                            <option value="minhas">Minhas chamadas</option>
                            <option value="todos">Todos os usuários</option>
                            {usuarios.map(u => <option key={u.id} value={`usuario:${u.id}`}>{u.name}</option>)}
                        </Select>
                    </Box>
                </HStack>
            )}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
                <FiltroCard label="Todas" value={contadores.total} ativo={filtro === "todas"} onClick={() => setFiltro("todas")} color="gray" />
                <FiltroCard label="Atendidas" value={contadores.atendidas} ativo={filtro === "atendidas"} onClick={() => setFiltro("atendidas")} color="green" />
                <FiltroCard label="Perdidas" value={contadores.perdidas} ativo={filtro === "perdidas"} onClick={() => setFiltro("perdidas")} color="red" icon={PhoneMissed} />
                <FiltroCard label="Saídas" value={contadores.saidas} ativo={filtro === "saidas"} onClick={() => setFiltro("saidas")} color="blue" />
            </SimpleGrid>
            <HStack mb={3} justify="space-between">
                <Text fontSize="sm" color="gray.500">{lista.length} chamada(s) {filtro !== "todas" && `(filtro: ${filtro})`}</Text>
                <IconButton size="xs" variant="ghost" icon={<RefreshCw size={12} />} onClick={carregar} aria-label="atualizar" />
            </HStack>
            <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
                {loadingHist ? (
                    <Box p={3}><Skeleton height="40px" mb={2} /><Skeleton height="40px" mb={2} /><Skeleton height="40px" /></Box>
                ) : lista.length === 0 ? (
                    <Text textAlign="center" fontSize="sm" color="gray.400" py={8}>Nenhuma chamada nesse filtro</Text>
                ) : (
                    <VStack align="stretch" spacing={0}>
                        {lista.map((c) => {
                            const ent = c.direcao === "entrada";
                            const badge = CHAMADA_STATUS_BADGE[c.status] || { label: c.status, color: "gray" };
                            const Icon = ent ? (c.status === "perdida" ? PhoneMissed : PhoneIncoming) : PhoneOutgoing;
                            const iconColor = c.status === "perdida" ? "#ef4444" : ent ? "#10b981" : "#3b82f6";
                            const temGravacao = !!c.gravacao_path;
                            const temTranscricao = !!c.transcricao;
                            return (
                                <Box key={c.id} px={3} py={3} borderBottom="1px solid" borderColor="gray.50" _hover={{ bg: "gray.50" }} cursor="pointer" onClick={() => abrirDetalhe(c)}>
                                    <HStack align="start" spacing={3}>
                                        <Box mt={1}><Icon size={14} color={iconColor} /></Box>
                                        <Box flex={1} minW={0}>
                                            <Text fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={1}>
                                                {c.cliente?.nome || fmtNumero(c.numero)}
                                            </Text>
                                            <HStack spacing={2} flexWrap="wrap">
                                                <Text fontSize="11px" color="gray.500">{fmtNumero(c.numero)}</Text>
                                                <Text fontSize="11px" color="gray.400">•</Text>
                                                <Text fontSize="11px" color="gray.500">{new Date(c.iniciada_em || c.created_at).toLocaleString("pt-BR")}</Text>
                                                {c.duracao_seg > 0 && <><Text fontSize="11px" color="gray.400">•</Text><Text fontSize="11px" color="gray.500">{fmtDuration(c.duracao_seg)}</Text></>}
                                                <Badge colorScheme={badge.color} fontSize="9px" borderRadius="sm">{badge.label}</Badge>
                                                {temGravacao && <Badge colorScheme="red" fontSize="9px" borderRadius="sm"><HStack spacing={1}><Radio size={8} /><Text>Gravada</Text></HStack></Badge>}
                                                {temTranscricao && <Badge colorScheme="purple" fontSize="9px" borderRadius="sm"><HStack spacing={1}><Sparkles size={8} /><Text>Transcrita</Text></HStack></Badge>}
                                                {c.user?.name && sp.isAdmin && <><Text fontSize="11px" color="gray.400">•</Text><Text fontSize="11px" color="gray.500">por {c.user.name}</Text></>}
                                            </HStack>
                                        </Box>
                                        <HStack onClick={(e) => e.stopPropagation()}>
                                            {!temTranscricao && temGravacao && (
                                                <Tooltip label="Transcrever com Whisper">
                                                    <IconButton size="xs" colorScheme="purple" variant="outline" icon={transcribingId === c.id ? <Spinner size="xs" /> : <Sparkles size={12} />} onClick={() => handleTranscrever(c)} isDisabled={transcribingId === c.id} aria-label="transcrever" />
                                                </Tooltip>
                                            )}
                                        </HStack>
                                    </HStack>
                                </Box>
                            );
                        })}
                    </VStack>
                )}
            </Box>

            <DetalheChamadaDialog
                isOpen={detalhe.open}
                onClose={() => setDetalhe({ open: false, chamada: null })}
                chamada={detalhe.chamada}
                onTranscrever={handleTranscrever}
                transcribingId={transcribingId}
            />
        </Box>
    );
};

const DetalheChamadaDialog = ({ isOpen, onClose, chamada, onTranscrever, transcribingId }) => {
    const cancelRef = React.useRef();
    const [audioUrl, setAudioUrl] = useState(null);
    const [loadingAudio, setLoadingAudio] = useState(false);

    const temGravacao = chamada && !!chamada.gravacao_path && (chamada.has_audio !== false);

    useEffect(() => {
        if (!isOpen || !temGravacao || !chamada) {
            setAudioUrl(null);
            return;
        }
        let revoke = null;
        let cancelled = false;
        setLoadingAudio(true);
        getSoftphoneAudioBlob(chamada.id).then((blob) => {
            setLoadingAudio(false);
            if (cancelled) return;
            if (blob) {
                const url = URL.createObjectURL(blob);
                revoke = url;
                setAudioUrl(url);
            }
        });
        return () => {
            cancelled = true;
            if (revoke) URL.revokeObjectURL(revoke);
            setAudioUrl(null);
        };
    }, [isOpen, chamada?.id, temGravacao]);

    if (!chamada) return null;
    const badge = CHAMADA_STATUS_BADGE[chamada.status] || { label: chamada.status, color: "gray" };

    return (
        <AlertDialog isOpen={isOpen} onClose={onClose} leastDestructiveRef={cancelRef} size="2xl" scrollBehavior="inside">
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader fontSize="md">
                        <HStack>
                            {chamada.direcao === "entrada" ? <PhoneIncoming size={16} color="#10b981" /> : <PhoneOutgoing size={16} color="#3b82f6" />}
                            <Text>{chamada.cliente?.nome || fmtNumero(chamada.numero)}</Text>
                            <Badge colorScheme={badge.color} fontSize="10px">{badge.label}</Badge>
                        </HStack>
                    </AlertDialogHeader>
                    <AlertDialogBody>
                        <SimpleGrid columns={2} spacing={2} mb={4}>
                            <DetailItem label="Número" value={fmtNumero(chamada.numero)} />
                            <DetailItem label="Direção" value={chamada.direcao === "entrada" ? "Recebida" : "Realizada"} />
                            <DetailItem label="Iniciada" value={new Date(chamada.iniciada_em || chamada.created_at).toLocaleString("pt-BR")} />
                            <DetailItem label="Duração" value={chamada.duracao_seg > 0 ? fmtDuration(chamada.duracao_seg) : "—"} />
                            {chamada.user?.name && <DetailItem label="Atendente" value={chamada.user.name} />}
                            {chamada.cliente?.nome && <DetailItem label="Cliente" value={chamada.cliente.nome} />}
                        </SimpleGrid>

                        {temGravacao ? (
                            <Box mb={4}>
                                <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" mb={2}>Gravação</Text>
                                {loadingAudio ? (
                                    <HStack p={3} bg="gray.50" borderRadius="md"><Spinner size="xs" /><Text fontSize="xs" color="gray.500">Carregando áudio...</Text></HStack>
                                ) : audioUrl ? (
                                    <audio controls src={audioUrl} style={{ width: "100%" }}>
                                        Seu navegador não suporta áudio.
                                    </audio>
                                ) : (
                                    <Box p={3} bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md">
                                        <Text fontSize="xs" color="red.700">Não foi possível carregar o áudio.</Text>
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Box mb={4} p={3} bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md">
                                <Text fontSize="xs" color="gray.500">Sem gravação disponível.</Text>
                            </Box>
                        )}

                        <Box>
                            <HStack mb={2} justify="space-between">
                                <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase">Transcrição (Whisper)</Text>
                                {!chamada.transcricao && temGravacao && (
                                    <Button size="xs" colorScheme="purple" leftIcon={transcribingId === chamada.id ? <Spinner size="xs" /> : <Sparkles size={12} />} onClick={() => onTranscrever(chamada)} isDisabled={transcribingId === chamada.id}>
                                        Transcrever
                                    </Button>
                                )}
                            </HStack>
                            {Array.isArray(chamada.transcricao_dialogo) && chamada.transcricao_dialogo.length > 0 ? (
                                <DialogoView dialogo={chamada.transcricao_dialogo} />
                            ) : (
                                <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={3} maxH="300px" overflowY="auto">
                                    <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">{chamada.transcricao || (temGravacao ? "Sem transcrição. Clique em \"Transcrever\" para gerar." : "Sem gravação para transcrever.")}</Text>
                                </Box>
                            )}
                        </Box>
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={onClose} size="sm">Fechar</Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
};

const DetailItem = ({ label, value }) => (
    <Box>
        <Text fontSize="10px" color="gray.500" fontWeight="semibold" textTransform="uppercase">{label}</Text>
        <Text fontSize="sm" color="gray.800">{value}</Text>
    </Box>
);

const fmtSegT = (t) => {
    const m = Math.floor(t / 60).toString().padStart(2, "0");
    const s = Math.floor(t % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
};

const DialogoView = ({ dialogo }) => (
    <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={3} maxH="450px" overflowY="auto">
        <VStack align="stretch" spacing={2}>
            {dialogo.map((d, i) => {
                const ehAtendente = d.speaker === "atendente";
                return (
                    <HStack key={i} align="flex-start" justify={ehAtendente ? "flex-end" : "flex-start"} spacing={2}>
                        {!ehAtendente && (
                            <Avatar size="xs" name="Cliente" bg="green.500" color="white" />
                        )}
                        <Box
                            maxW="78%"
                            bg={ehAtendente ? "blue.500" : "white"}
                            color={ehAtendente ? "white" : "gray.800"}
                            border={ehAtendente ? "none" : "1px solid"}
                            borderColor="gray.200"
                            borderRadius="lg"
                            borderBottomRightRadius={ehAtendente ? "sm" : "lg"}
                            borderBottomLeftRadius={ehAtendente ? "lg" : "sm"}
                            px={3}
                            py={2}
                            boxShadow="sm"
                        >
                            <HStack mb={1} spacing={2} justify="space-between">
                                <Text fontSize="9px" fontWeight="bold" textTransform="uppercase" opacity={0.85} letterSpacing="wide">
                                    {ehAtendente ? "Atendente" : "Cliente"}
                                </Text>
                                <Text fontSize="9px" opacity={0.7} fontFamily="mono">{fmtSegT(d.t_inicio || 0)}</Text>
                            </HStack>
                            <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="1.4">{d.texto}</Text>
                        </Box>
                        {ehAtendente && (
                            <Avatar size="xs" name="Atendente" bg="blue.500" color="white" />
                        )}
                    </HStack>
                );
            })}
        </VStack>
    </Box>
);

// ===================== Aba Configurações =====================
const ConfiguracoesPanel = ({ sp }) => {
    const toast = useToast();
    const [draft, setDraft] = useState(sp.config);
    const [saving, setSaving] = useState(false);
    const [devices, setDevices] = useState({ inputs: [], outputs: [] });

    useEffect(() => setDraft(sp.config), [sp.config]);

    useEffect(() => {
        (async () => {
            try {
                await navigator.mediaDevices.getUserMedia({ audio: true }).then((s) => s.getTracks().forEach((t) => t.stop()));
                const all = await navigator.mediaDevices.enumerateDevices();
                setDevices({
                    inputs: all.filter((d) => d.kind === "audioinput"),
                    outputs: all.filter((d) => d.kind === "audiooutput"),
                });
            } catch (_) {}
        })();
    }, []);

    const update = (patch) => setDraft((d) => ({ ...d, ...patch }));
    const updateDias = (dia) => {
        const has = (draft.dias_semana || []).includes(dia);
        const novo = has ? draft.dias_semana.filter((x) => x !== dia) : [...(draft.dias_semana || []), dia].sort();
        update({ dias_semana: novo });
    };

    const salvar = async () => {
        setSaving(true);
        const res = await sp.saveConfig(draft);
        setSaving(false);
        if (res?.data) toast({ status: "success", title: "Configurações salvas" });
        else toast({ status: "error", title: res?.error || "Falha ao salvar" });
    };

    const dirty = JSON.stringify(draft) !== JSON.stringify(sp.config);

    return (
        <VStack align="stretch" spacing={6}>
            {/* Recepção */}
            <Section icon={<Bell size={14} />} title="Recepção de chamadas">
                <FormControl>
                    <FormLabel fontSize="xs" color="gray.600" fontWeight="semibold">Receber chamadas em:</FormLabel>
                    <Select size="sm" value={draft.modo_recepcao} onChange={(e) => update({ modo_recepcao: e.target.value })}>
                        <option value="aba_ativa">Apenas na tela /discador</option>
                        <option value="widget_global">Widget flutuante (qualquer tela)</option>
                        <option value="ambos">Ambos</option>
                    </Select>
                </FormControl>
                <Toggle label="Atender automaticamente" checked={!!draft.auto_atender} onChange={(v) => update({ auto_atender: v })} />
                {draft.auto_atender && (
                    <FormControl>
                        <FormLabel fontSize="xs" color="gray.600">Após X segundos</FormLabel>
                        <Input size="sm" type="number" min={0} max={30} value={draft.auto_atender_apos_seg} onChange={(e) => update({ auto_atender_apos_seg: Number(e.target.value) })} w="100px" />
                    </FormControl>
                )}
            </Section>

            {/* Conexão automática */}
            <Section icon={<Calendar size={14} />} title="Conexão automática">
                <Toggle label="Conectar ao abrir o sistema" checked={!!draft.auto_conectar_ao_abrir} onChange={(v) => update({ auto_conectar_ao_abrir: v })} />
                <Toggle label="Conectar em horário específico" checked={!!draft.auto_conectar_horario} onChange={(v) => update({ auto_conectar_horario: v })} />
                {draft.auto_conectar_horario && (
                    <VStack align="stretch" pl={6} spacing={3}>
                        <HStack>
                            <FormControl><FormLabel fontSize="xs" color="gray.600">Início</FormLabel><Input size="sm" type="time" value={draft.horario_inicio} onChange={(e) => update({ horario_inicio: e.target.value })} /></FormControl>
                            <FormControl><FormLabel fontSize="xs" color="gray.600">Fim</FormLabel><Input size="sm" type="time" value={draft.horario_fim} onChange={(e) => update({ horario_fim: e.target.value })} /></FormControl>
                        </HStack>
                        <Box>
                            <Text fontSize="xs" color="gray.600" fontWeight="semibold" mb={2}>Dias da semana</Text>
                            <HStack flexWrap="wrap">
                                {DIAS.map((d) => (
                                    <Button key={d.id} size="xs" variant={draft.dias_semana?.includes(d.id) ? "solid" : "outline"} colorScheme={draft.dias_semana?.includes(d.id) ? "blue" : "gray"} onClick={() => updateDias(d.id)}>{d.label}</Button>
                                ))}
                            </HStack>
                        </Box>
                    </VStack>
                )}
                <Toggle label="Reconectar automaticamente se cair" checked={!!draft.tentar_reconectar} onChange={(v) => update({ tentar_reconectar: v })} />
                <FormControl>
                    <FormLabel fontSize="xs" color="gray.600">Verificar conexão a cada</FormLabel>
                    <HStack>
                        <Input size="sm" type="number" min={5} max={120} value={draft.heartbeat_seg} onChange={(e) => update({ heartbeat_seg: Number(e.target.value) })} w="100px" />
                        <Text fontSize="xs" color="gray.500">segundos (5-120)</Text>
                    </HStack>
                </FormControl>
            </Section>

            {/* Notificações */}
            <Section icon={<Bell size={14} />} title="Notificações e som">
                <Toggle label="Mostrar notificação do navegador" checked={!!draft.notificacao_browser} onChange={(v) => update({ notificacao_browser: v })} />
                <Toggle label="Tocar som ao receber chamada" checked={!!draft.notificacao_som} onChange={(v) => update({ notificacao_som: v })} />
                <SliderField label="Volume do toque" value={draft.volume_toque} onChange={(v) => update({ volume_toque: v })} />
                <SliderField label="Volume da chamada" value={draft.volume_chamada} onChange={(v) => update({ volume_chamada: v })} />
                <Toggle label="Mostrar pop-up do cliente ao receber chamada" checked={!!draft.mostrar_popup_cliente} onChange={(v) => update({ mostrar_popup_cliente: v })} />
            </Section>

            {/* Dispositivos */}
            <Section icon={<Headphones size={14} />} title="Dispositivos de áudio">
                <FormControl>
                    <FormLabel fontSize="xs" color="gray.600">Microfone</FormLabel>
                    <Select size="sm" value={draft.microfone_device_id || ""} onChange={(e) => update({ microfone_device_id: e.target.value || null })}>
                        <option value="">Padrão do sistema</option>
                        {devices.inputs.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || "Microfone " + d.deviceId.slice(0,6)}</option>)}
                    </Select>
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="xs" color="gray.600">Saída de áudio (fone/alto-falante)</FormLabel>
                    <Select size="sm" value={draft.autofalante_device_id || ""} onChange={(e) => update({ autofalante_device_id: e.target.value || null })}>
                        <option value="">Padrão do sistema</option>
                        {devices.outputs.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || "Saída " + d.deviceId.slice(0,6)}</option>)}
                    </Select>
                    <Text fontSize="10px" color="gray.400" mt={1}>Em alguns navegadores a seleção de saída requer Chrome/Edge.</Text>
                </FormControl>
            </Section>

            {/* Transcrição global */}
            <GlobalTranscricaoSection sp={sp} />

            <HStack justify="flex-end" pt={2}>
                <Button size="sm" variant="ghost" onClick={() => setDraft(sp.config)} isDisabled={!dirty || saving}>Reverter</Button>
                <Button size="sm" colorScheme="blue" leftIcon={<Save size={14} />} onClick={salvar} isLoading={saving} loadingText="Salvando" isDisabled={!dirty}>Salvar configurações</Button>
            </HStack>
        </VStack>
    );
};

const GlobalTranscricaoSection = ({ sp }) => {
    const toast = useToast();
    const [draft, setDraft] = useState(sp.globalConfig);
    const [saving, setSaving] = useState(false);
    useEffect(() => setDraft(sp.globalConfig), [sp.globalConfig]);
    const update = (patch) => setDraft((d) => ({ ...d, ...patch }));
    const dirty = JSON.stringify(draft) !== JSON.stringify(sp.globalConfig);

    const salvar = async () => {
        setSaving(true);
        const res = await sp.saveGlobalConfig(draft);
        setSaving(false);
        if (res?.data) toast({ status: "success", title: "Configuração global salva (afeta todos os usuários)" });
        else toast({ status: "error", title: res?.error || "Falha ao salvar" });
    };

    return (
        <Section icon={<Sparkles size={14} />} title="Transcrição & Gravação (configuração GLOBAL)">
            <Box bg={sp.isAdmin ? "blue.50" : "gray.50"} border="1px solid" borderColor={sp.isAdmin ? "blue.200" : "gray.200"} p={3} borderRadius="md">
                <Text fontSize="xs" color={sp.isAdmin ? "blue.800" : "gray.600"} fontWeight="semibold">
                    {sp.isAdmin
                        ? "Você é administrador — estas opções afetam TODOS os usuários do sistema."
                        : "Estas configurações são globais. Apenas administradores podem editar."}
                </Text>
            </Box>
            <Box bg="yellow.50" border="1px solid" borderColor="yellow.200" p={3} borderRadius="md">
                <Text fontSize="xs" color="yellow.800" fontWeight="semibold" mb={1}>Aviso legal</Text>
                <Text fontSize="xs" color="yellow.700">Ao habilitar gravação, a empresa se compromete a informar a outra parte que a ligação está sendo gravada.</Text>
            </Box>
            <Toggle label="Aceitar termos de gravação (libera o uso em toda a empresa)" checked={!!draft.aceitou_termos_gravacao} onChange={(v) => update({ aceitou_termos_gravacao: v })} isDisabled={!sp.isAdmin} />
            <Toggle label="Gravar e transcrever AUTOMATICAMENTE todas as chamadas" checked={!!draft.transcrever_auto} onChange={(v) => update({ transcrever_auto: v })} isDisabled={!sp.isAdmin || !draft.aceitou_termos_gravacao} />
            <FormControl>
                <FormLabel fontSize="xs" color="gray.600">Idioma da transcrição</FormLabel>
                <Select size="sm" value={draft.idioma_transcricao} onChange={(e) => update({ idioma_transcricao: e.target.value })} isDisabled={!sp.isAdmin}>
                    <option value="pt">Português</option>
                    <option value="en">English</option>
                    <option value="es">Español</option>
                </Select>
            </FormControl>
            {sp.isAdmin && (
                <HStack justify="flex-end">
                    <Button size="xs" variant="ghost" onClick={() => setDraft(sp.globalConfig)} isDisabled={!dirty || saving}>Reverter</Button>
                    <Button size="xs" colorScheme="purple" leftIcon={<Save size={12} />} onClick={salvar} isLoading={saving} loadingText="Salvando" isDisabled={!dirty}>Salvar global</Button>
                </HStack>
            )}
        </Section>
    );
};

const FiltroCard = ({ label, value, ativo, onClick, color = "gray", icon: Icon }) => (
    <Box
        as="button"
        onClick={onClick}
        bg={ativo ? `${color}.50` : "white"}
        border="1px solid"
        borderColor={ativo ? `${color}.300` : "gray.200"}
        borderRadius="md"
        p={3}
        textAlign="left"
        cursor="pointer"
        transition="all 0.15s"
        _hover={{ borderColor: `${color}.300`, bg: `${color}.50` }}
    >
        <HStack mb={1}>
            {Icon && <Icon size={12} color={`var(--chakra-colors-${color}-500)`} />}
            <Text fontSize="10px" color={`${color}.600`} fontWeight="semibold" textTransform="uppercase">{label}</Text>
        </HStack>
        <Text fontSize="xl" fontWeight="bold" color={`${color}.700`}>{value}</Text>
    </Box>
);

const Section = ({ icon, title, children }) => (
    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={4} bg="white">
        <HStack mb={3}>{icon}<Text fontSize="sm" fontWeight="bold" color="gray.800">{title}</Text></HStack>
        <VStack align="stretch" spacing={3}>{children}</VStack>
    </Box>
);

const Toggle = ({ label, checked, onChange, isDisabled }) => (
    <HStack justify="space-between">
        <Text fontSize="sm" color="gray.700">{label}</Text>
        <Switch colorScheme="blue" isChecked={!!checked} onChange={(e) => onChange(e.target.checked)} isDisabled={isDisabled} />
    </HStack>
);

const SliderField = ({ label, value, onChange }) => (
    <FormControl>
        <HStack justify="space-between"><FormLabel fontSize="xs" color="gray.600" mb={0}>{label}</FormLabel><Text fontSize="xs" color="gray.500">{value}%</Text></HStack>
        <Slider value={value} min={0} max={100} step={5} onChange={onChange} colorScheme="blue">
            <SliderTrack><SliderFilledTrack /></SliderTrack>
            <SliderThumb />
        </Slider>
    </FormControl>
);

export default Discador;
